import React, { useState } from "react";
import { TEMPLATES, TEMPLATE_BY_ID, pickTemplateFor } from "../lib/reportTemplates";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Sparkles, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Doctor-facing structured opinion editor.
 *
 * Renders a condition-specific template (selected automatically from the
 * case's symptom tags, but switchable). Each field has an inline "✨ Suggest"
 * button that calls `/ai/opinion-suggest` for that field.
 *
 * Props:
 *   consultation: { id, symptom_tags, symptoms, ... }
 *   value: { template, fields }
 *   onChange: (next) => void
 */
export default function StructuredOpinionEditor({ consultation, value, onChange }) {
  const initialTplId = value?.template || pickTemplateFor(consultation);
  const [tplId, setTplId] = useState(initialTplId);
  const tpl = TEMPLATE_BY_ID[tplId] || TEMPLATE_BY_ID.general;
  const [busyField, setBusyField] = useState(null);

  const fields = value?.fields || {};

  const setField = (key, v) => {
    onChange?.({ template: tpl.id, fields: { ...fields, [key]: v } });
  };

  const switchTemplate = (id) => {
    setTplId(id);
    onChange?.({ template: id, fields });
  };

  const suggest = async (section) => {
    setBusyField(section.key);
    try {
      const r = await api.post("/ai/opinion-suggest", {
        consultation_id: consultation?.id,
        template: tpl.id,
        field: section.aiKey || section.key,
        existing_value: fields[section.key] || "",
      });
      if (!r.data.ok) { toast.error(r.data.error || "AI unavailable"); return; }
      setField(section.key, r.data.suggestion);
      toast.success("Suggestion applied — edit as needed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suggest failed");
    } finally { setBusyField(null); }
  };

  return (
    <div className="space-y-5" data-testid="structured-opinion-editor">
      {/* Template selector — horizontal scroll on mobile, wrap on desktop */}
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Report template</Label>
        <div className="mt-2 flex gap-1.5 flex-wrap" data-testid="template-selector">
          {TEMPLATES.map((t) => {
            const Icon = t.icon;
            const active = t.id === tpl.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => switchTemplate(t.id)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-semibold transition ${
                  active
                    ? "bg-blue-700 border-blue-700 text-white shadow-sm"
                    : "bg-white border-slate-300 text-slate-700 hover:border-blue-400"
                }`}
                data-testid={`template-${t.id}`}
              >
                <Icon className="w-3.5 h-3.5"/> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Section fields */}
      {tpl.sections.map((section) => {
        const isTextarea = section.type === "textarea";
        const Comp = isTextarea ? Textarea : Input;
        const busy = busyField === section.key;
        return (
          <div key={section.key}>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
              <Label className="text-sm font-semibold text-slate-900">{section.label}</Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => suggest(section)}
                className="h-7 px-2 text-[11px] font-semibold text-blue-700 hover:bg-blue-50"
                data-testid={`suggest-${section.key}`}
              >
                {busy
                  ? <><Loader2 className="w-3 h-3 mr-1 animate-spin"/> Thinking…</>
                  : <><Sparkles className="w-3 h-3 mr-1"/> Suggest</>}
              </Button>
            </div>
            <Comp
              rows={isTextarea ? 3 : undefined}
              value={fields[section.key] || ""}
              onChange={(e) => setField(section.key, e.target.value)}
              placeholder={section.placeholder || ""}
              data-testid={`field-${section.key}`}
              className="text-sm"
            />
          </div>
        );
      })}
    </div>
  );
}
